/**
 * "Dados da conta" on the person record (D580): what the account holds (contacts, profiles, language, time zone, when it was created and last
 * changed) and — read on its own — how it was registered (the person signed up, or an operator registered it and why) with the last phone
 * changes. The country is not a row: it is the console's scope, not a fact about the person (owner, 2026-10-13).
 */
import { type ReactElement } from 'react';
import { type DirectoryUser } from '@vello/api-client';
import { Badge, Ink, KeyValueList, RecordPanel, RecordRow, RecordSection, Text } from '@vello/web-theme';
import { shownEmail, shownPhone } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { CardBody, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

/** How many phone changes the card lists (newest first). */
const PHONE_CHANGES_SHOWN = 3;

export function PersonAccountCard({ user }: { user: DirectoryUser }): ReactElement {
  const { runtime, scope } = useAdmin();
  const { t } = useT();
  const { v, language } = useVocab();
  const fmt = usePersonFormat();
  const { state, reload } = useCardRead(() => runtime.accountRegistry.registration(user.id), [runtime, user.id]);

  const rows: readonly (readonly [string, string, boolean])[] = [
    [t('dir.col.phone'), shownPhone(user, scope.current.country), true],
    [t('dir.col.email'), shownEmail(user) ?? '—', false],
    [t('dir.col.realms'), user.realms.map((r) => v('identity.realm', r)).join(' · '), false],
    [t('prec.account.language'), user.locale !== undefined ? language(user.locale) : '—', false],
    [t('prec.account.timezone'), user.timezone ?? '—', true],
    [t('dir.col.since'), fmt.when(user.createdAt, 'datetime-seconds'), true],
    [t('prec.account.updated'), fmt.when(user.updatedAt, 'datetime-seconds'), true],
    [t('dir.col.id'), user.id, true],
  ];

  return (
    <RecordPanel title={t('person.account')}>
      <KeyValueList items={rows.map(([label, value, mono]) => ({ label, value, mono }))} />
      <RecordSection>
        <Text as="h3" role="label">
          {t('prec.account.registration')}
        </Text>
        <CardBody what={t('prec.what.registration')} state={state} reload={reload}>
          {({ registration, phoneChanges }) => (
            <>
              {registration === null ? (
                <Text as="p" role="caption" tone="secondary">
                  {t('prec.account.selfSignup')}
                </Text>
              ) : (
                <KeyValueList
                  dense
                  items={[
                    { label: t('prec.account.channel'), value: v('account.registration_channel', registration.channel) },
                    { label: t('prec.account.reason'), value: v('account.rider_registration_reason', registration.reasonCode) },
                    { label: t('prec.account.registeredBy'), value: registration.registeredBy, mono: true },
                    { label: t('prec.account.registeredAt'), value: fmt.when(registration.registeredAt), mono: true },
                    { label: t('prec.account.claimed'), value: t(registration.claimed ? 'prec.account.claimed.yes' : 'prec.account.claimed.no') },
                    ...(registration.note !== undefined && registration.note !== '' ? [{ label: t('prec.account.note'), value: registration.note }] : []),
                  ]}
                />
              )}
              {phoneChanges.length > 0 && (
                <>
                  <Text as="h4" role="caption" tone="secondary">
                    {t('prec.account.phoneChanges')}
                  </Text>
                  {phoneChanges.slice(0, PHONE_CHANGES_SHOWN).map((c) => (
                    <RecordRow key={c.id} dense>
                      <Badge tone={c.state === 'APPLIED' ? 'success' : c.state === 'PENDING' ? 'warning' : 'neutral'}>{v('identity.phone_change_state', c.state)}</Badge>
                      <span>{v('account.phone_change_reason', c.reasonCode)}</span>
                      <span className="vello-grow" />
                      <Ink mono>{fmt.when(c.proposedAt)}</Ink>
                    </RecordRow>
                  ))}
                </>
              )}
            </>
          )}
        </CardBody>
      </RecordSection>
    </RecordPanel>
  );
}
